import { useQueryClient } from '@tanstack/react-query';
import { MindspaceDashboardCard } from '@/components/features/mindspace/cards/MindspaceDashboardCard';
import { CardSwap, Card } from '@/components/core/animations/CardSwap';
import { FitBox } from '@/components/core/animations/FitBox';
import { FlashcardContent } from '@/components/features/mindspace/cards/FlashcardContent';
import { mindspaceApi } from '@/lib/api-client';
import { revisionKeys } from '@/hooks/mindspace/useRevision';
import { useDataMode } from '@/contexts/DataModeContext';

// Static preview content for the dashboard card's flashcard animation.
//
// This used to be `Object.values(mockConceptDetails).slice(0, 4)`, which pulled
// the whole fixture module into the production bundle — for four decorative
// strings, on a card that is not data-mode gated and shows this filler to every
// user regardless of mode. The fallbacks were already hard-coded inline
// (`|| 'Neural Networks'`), so the fixture was only ever supplying the
// descriptions. Local placeholder content is both smaller and honest about
// what it is.
const flashcardConcepts = [
  {
    title: 'Neural Networks',
    description:
      'Layers of weighted connections that learn patterns from examples.',
  },
  {
    title: 'Machine Learning',
    description:
      'Fitting a model to data so it generalises to inputs it has not seen.',
  },
  {
    title: 'Deep Learning',
    description:
      'Many-layered networks that learn their own feature representations.',
  },
] as const;

const CARD_W = 220;
const CARD_H = 160;
const FAN_X = 22;
const FAN_Y = 28;
/** Cards behind the front one. */
const BEHIND = 3;
// The fan, skew and shadow included: the back card is BEHIND x FAN offset up
// and right of the front one, and the elastic swap overshoots a little.
const STACK_W = CARD_W + BEHIND * FAN_X + 24;
const STACK_H = CARD_H + BEHIND * FAN_Y + 24;

export function PracticeArenaCard() {
  const qc = useQueryClient();
  const { isMock, isEmpty } = useDataMode();

  // Warm the React Query cache when the user shows intent (hover / focus
  // the card). The Practice Arena setup screen fires up to five queries
  // on mount (daily plan + four preview queues) - prefetching the most
  // expensive ones makes navigation feel instant. We skip in mock/empty
  // modes since they short-circuit to canned data anyway.
  const handlePrefetch = () => {
    if (isMock || isEmpty) return;
    void qc.prefetchQuery({
      queryKey: revisionKeys.dailyPlan(),
      queryFn: () => mindspaceApi.getDailyPlan(),
      staleTime: 4 * 60 * 60 * 1000,
    });
    void qc.prefetchQuery({
      queryKey: revisionKeys.queue('practice', {
        limit: 4,
        questionsPerConcept: 0,
      }),
      queryFn: () =>
        mindspaceApi.getPracticeQueue({
          limit: 4,
          questions_per_concept: 0,
        }),
      staleTime: 60 * 1000,
    });
    void qc.prefetchQuery({
      queryKey: revisionKeys.queue('practice', {
        limit: 4,
        questionsPerConcept: 0,
        bookmarked: true,
      }),
      queryFn: () =>
        mindspaceApi.getPracticeQueue({
          limit: 4,
          questions_per_concept: 0,
          bookmarked: true,
        }),
      staleTime: 60 * 1000,
    });
  };

  return (
    <MindspaceDashboardCard
      title='Practice Arena'
      subtitle='Flashcards, quizzes & drills'
      actionPath='/mindspace/practice-arena'
      actionLabel='Practice'
      onPrefetch={handlePrefetch}
    >
      {/* The stack's whole footprint, scaled to fit the card. `CardSwap`
          centres the FRONT card in its box and fans the rest up and to the
          right, so a box sized to one card left the fan to climb over the
          card's title. Here the box is the fan's size, the front card sits
          at its bottom-left, and `FitBox` shrinks the lot on short screens.
          Four cards (two question/answer pairs), not six: each one more is
          another 28px of fan. */}
      <div className='h-full min-h-[180px] w-full py-2'>
        <FitBox width={STACK_W} height={STACK_H}>
          <div
            className='absolute'
            style={{
              left: CARD_W / 2,
              top: STACK_H - CARD_H / 2,
            }}
          >
            <div
              className='absolute'
              style={{ left: -CARD_W / 2, top: -CARD_H / 2 }}
            >
              <CardSwap
                width={CARD_W}
                height={CARD_H}
                cardDistance={FAN_X}
                verticalDistance={FAN_Y}
                delay={4000}
                initialDelay={1000}
                pauseOnHover={false}
                skewAmount={3}
                easing='elastic'
              >
                <Card>
                  <FlashcardContent
                    title={flashcardConcepts[0].title}
                    isFront={true}
                  />
                </Card>
                <Card>
                  <FlashcardContent
                    title={flashcardConcepts[0].title}
                    description={flashcardConcepts[0].description}
                    isFront={false}
                  />
                </Card>
                <Card>
                  <FlashcardContent
                    title={flashcardConcepts[1].title}
                    isFront={true}
                  />
                </Card>
                <Card>
                  <FlashcardContent
                    title={flashcardConcepts[1].title}
                    description={flashcardConcepts[1].description}
                    isFront={false}
                  />
                </Card>
              </CardSwap>
            </div>
          </div>
        </FitBox>
      </div>
    </MindspaceDashboardCard>
  );
}
